<script setup lang="ts">
import gsap from 'gsap'
import { SplitText } from 'gsap/SplitText'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

const props = defineProps<{
  data: SectionContactRecord
  moduleIndex: number
}>()

const { level, isSemantic, invertedStyles } = useSectionRules(
  props.moduleIndex,
  props.data.invertColorTheme,
  !!props.data.title
)
const ctxRef = ref<HTMLElement | null>(null)
const ctxFrame = ref<HTMLElement | null>(null)

let ctx: gsap.Context | null = null
let tl: GSAPTimeline | null = null

onMounted(() => {
  if (ctxRef.value) {
    gsap.registerPlugin(ScrollTrigger, SplitText)

    ctx = gsap.context(() => {
      // Text Reveal
      const split = new SplitText('.fx-text-reveal', { type: 'lines' })

      split.lines.forEach((target) => {
        gsap.to(target, {
          backgroundPositionX: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: target,
            markers: false,
            scrub: 1,
            start: 'top 60%',
            end: 'bottom 60%'
          }
        })
      })

      // Animated Frames
      gsap.set('.fx-frame-l', { clipPath: 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)' })
      gsap.set('.fx-frame-r', { clipPath: 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)' })
      gsap.set('.fx-frame-y', { clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)' })

      tl = gsap.timeline({
        scrollTrigger: {
          trigger: ctxFrame.value,
          start: 'center bottom'
        }
      })
      tl.to('.fx-frame-l, .fx-frame-r, .fx-frame-y', {
        duration: 1.5,
        clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
        ease: 'power4.out'
      })
    }, ctxRef.value as HTMLElement)
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div ref="ctxRef" class="py-12" :class="invertedStyles">
    <div class="container grid-default relative gap-y-12 lg:gap-y-18">
      <div class="col-span-full xl:col-span-10 xl:col-start-2">
        <div ref="ctxFrame" class="relative mx-auto max-w-xs py-[9rem] lg:max-w-none lg:py-0">
          <div
            class="flex w-full flex-col items-center gap-1 lg:h-[12.688rem] lg:flex-row lg:justify-between lg:gap-5 lg:px-28"
          >
            <svg
              class="fx-frame-y absolute left-1/2 top-0 -translate-x-1/2 mix-blend-difference invert lg:hidden"
              width="345"
              height="170"
              viewBox="0 0 345 170"
              fill="none"
            >
              <path
                d="M1.5 169.529V139.94C1.5 127.27 11.8013 116.999 24.5081 116.999H126.365"
                stroke="#1D1D1B"
                stroke-width="2"
                stroke-miterlimit="10"
              />
              <path
                d="M219.637 117.003H322.479C334.364 117.003 344 127.274 344 139.944V169.533"
                stroke="#1D1D1B"
                stroke-width="2"
                stroke-miterlimit="10"
              />
              <path d="M172.499 0L172.499 116.93" stroke="black" stroke-width="2" />
            </svg>

            <svg
              class="fx-frame-y absolute bottom-0 left-1/2 -translate-x-1/2 mix-blend-difference invert lg:hidden"
              width="345"
              height="170"
              viewBox="0 0 345 170"
              fill="none"
            >
              <path
                d="M344 0.00390625V29.5934C344 42.2631 333.684 52.5343 320.958 52.5343L218.953 52.5343"
                stroke="#1D1D1B"
                stroke-width="2"
                stroke-miterlimit="10"
              />
              <path
                d="M125.545 52.5304L22.5529 52.5304C10.6499 52.5304 1.00027 42.2592 1.00027 29.5895V0"
                stroke="#1D1D1B"
                stroke-width="2"
                stroke-miterlimit="10"
              />
              <path d="M172.751 169.533L172.751 52.6032" stroke="black" stroke-width="2" />
            </svg>

            <svg
              class="fx-frame-l absolute left-1/2 top-0 hidden origin-center -translate-x-1/2 rotate-90 mix-blend-difference invert lg:left-0 lg:block lg:translate-x-0 lg:rotate-0"
              width="135"
              height="203"
              viewBox="0 0 135 203"
              fill="none"
            >
              <path
                d="M82 53.5366V23.9409C82 11.2712 92.2712 1 104.941 1H134.537"
                stroke="#1D1D1B"
                stroke-width="2"
                stroke-miterlimit="10"
              />
              <path
                d="M134.537 202H104.941C92.2712 202 82 191.729 82 179.059V149.47"
                stroke="#1D1D1B"
                stroke-width="2"
                stroke-miterlimit="10"
              />
              <path d="M-34 101.5L82.5 101.5" stroke="black" stroke-width="2" />
            </svg>
            <Heading
              v-if="data.title"
              class="text-center text-2xl font-bold lg:max-w-sm lg:text-left lg:text-3xl-b"
              :level="level"
              :size="undefined"
              :is-semantic="isSemantic"
            >
              <TextReveal>
                {{ data.title }}
              </TextReveal>
            </Heading>
            <div
              v-if="data.subtitle"
              class="fx-text-reveal text-center font-semibold lg:max-w-[14rem] lg:text-right lg:text-lg lg:leading-snug"
            >
              {{ data.subtitle }}
            </div>
            <svg
              class="fx-frame-r absolute bottom-0 left-1/2 hidden -translate-x-1/2 rotate-90 mix-blend-difference invert lg:left-auto lg:right-0 lg:block lg:translate-x-0 lg:-rotate-0"
              width="135"
              height="203"
              viewBox="0 0 135 203"
              fill="none"
            >
              <path
                d="M52.5371 149.463L52.5371 179.059C52.5371 191.729 42.2659 202 29.5962 202L0.000499277 202"
                stroke="#1D1D1B"
                stroke-width="2"
                stroke-miterlimit="10"
              />
              <path
                d="M0.000503563 0.999866L29.5962 0.999868C42.2659 0.999869 52.5371 11.2711 52.5371 23.9408L52.5371 53.5303"
                stroke="#1D1D1B"
                stroke-width="2"
                stroke-miterlimit="10"
              />
              <path d="M168.537 101.5L52.0371 101.5" stroke="black" stroke-width="2" />
            </svg>
          </div>
        </div>
        <template v-if="Array.isArray(data.buttons) && data.buttons.length">
          <Button
            v-for="button in data.buttons"
            :key="button.id"
            :to="button.reference?.slug ?? undefined"
            variant="primary"
            class="absolute bottom-12 left-1/2 hidden -translate-x-1/2 uppercase lg:bottom-6 lg:inline-block lg:text-2xl lg:font-bold"
            ><TextReveal>
              {{ button.label }}
            </TextReveal>
          </Button>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="css">
.fx-text-reveal > div {
  background: linear-gradient(to right, #ff69e7 25%, #68e4ff 50%, rgb(37, 37, 37) 50%);
  background-size: 200% 100%;
  background-position-x: 100%;
  color: transparent;
  background-clip: text;
  -webkit-background-clip: text;
}
</style>
